import {
  TRIGGER_ORDER_STARTED,
  TRIGGER_ORDER_PRODUCT_SELECTED,
  TRIGGER_ORDER_DETAILS_ENTERED,
  TRIGGER_ORDER_ADD_FUNDS_CLICKED,
  TRIGGER_ORDER_REVIEWED,
  TRIGGER_ORDER_PLACED,
  GTT_BUY_STARTED_BANNER,
  GTT_BUY_STARTED_BUY_PAGE,
} from '../../../constants/gtmEvents';

/**
 * Centralized GTT (Buy-at-Target-Price / trigger-order) GTM event dispatchers.
 *
 * Mirrors the price-alert gtmEventHelper convention: each function receives the
 * `ecommerceGtmEvent` dispatcher (from `useGtmHook()`) and builds the payload,
 * so the event names and payload shapes live in one place rather than inline
 * across the flow's screens.
 */

// How the trigger-order flow was entered. A single vocabulary so call sites
// can't drift on spelling.
export const ENTRY_POINTS = {
  HEADER: 'header', // "Buy at Target Price" nav item
  PRODUCT_CARD: 'product_card', // per-product card button (future)
  BUY_PAGE: 'buy_page', // Buy-page card
  TOP_NAV_BANNER: 'top_nav_banner', // top-nav "Buy at Target Price" banner
};

// trigger_order_started — the user opened the Buy-at-Target-Price flow.
export const trackTriggerOrderStarted = ({ ecommerceGtmEvent, entryPoint }) => {
  ecommerceGtmEvent(TRIGGER_ORDER_STARTED, {
    ...(entryPoint != null && { entry_point: entryPoint }),
  });
};

// trigger_order_product_selected — the user picked a product and advanced past
// the product-selection screen.
export const trackTriggerOrderProductSelected = ({
  ecommerceGtmEvent,
  productCode,
  productName,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_PRODUCT_SELECTED, {
    product_code: productCode,
    product_name: productName,
  });
};

// trigger_order_details_entered — the user set the target price, quantity and
// vault and advanced past the order-entry screen.
export const trackTriggerOrderDetailsEntered = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  vault,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_DETAILS_ENTERED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    vault,
  });
};

// trigger_order_add_funds_clicked — the entered target price × quantity
// exceeded the available balance, so the order-entry CTA relabeled to "Add
// Funds" and the user clicked it to open the deposit wizard. Fired on click
// only: the deposit wizards report no completion, so there is no success
// signal to hang a follow-up event on.
export const trackTriggerOrderAddFundsClicked = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  shortfall,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_ADD_FUNDS_CLICKED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    ...(shortfall != null && { shortfall }),
  });
};

// trigger_order_reviewed — the user reviewed the order summary and advanced
// past the review screen.
export const trackTriggerOrderReviewed = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  vault,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_REVIEWED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    vault,
  });
};

// trigger_order_placed — the order-placed confirmation screen was shown.
export const trackTriggerOrderPlaced = ({
  ecommerceGtmEvent,
  productCode,
  targetPrice,
  quantity,
  vault,
}) => {
  ecommerceGtmEvent(TRIGGER_ORDER_PLACED, {
    product_code: productCode,
    target_price: targetPrice,
    quantity,
    vault,
  });
};

// gtt_buy_started_banner — the user tapped "Buy at Target Price" from the top
// nav banner.
export const trackGttBuyStartedBanner = ({
  ecommerceGtmEvent,
  userId,
  accountType,
  verified,
}) => {
  ecommerceGtmEvent(GTT_BUY_STARTED_BANNER, {
    entry_point: ENTRY_POINTS.TOP_NAV_BANNER,
    user_id: userId,
    account_type: accountType,
    verified,
    timestamp: new Date().toISOString(),
  });
};

// gtt_buy_started_buy_page — the user tapped "Buy at Target Price" on a
// product card on the Buy page.
export const trackGttBuyStartedBuyPage = ({
  ecommerceGtmEvent,
  productId,
  productName,
  weight,
  currentPrice,
  userId,
  accountType,
  verified,
}) => {
  ecommerceGtmEvent(GTT_BUY_STARTED_BUY_PAGE, {
    entry_point: ENTRY_POINTS.BUY_PAGE,
    product_id: productId,
    product_name: productName,
    weight,
    current_price: currentPrice,
    user_id: userId,
    account_type: accountType,
    verified,
    timestamp: new Date().toISOString(),
  });
};
